import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity,
  CreditCard,
  Loader2,
  MailCheck,
  MousePointerClick,
  Play,
  RefreshCw,
  Search,
  UserCheck,
  Users,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

import { API, authHeaders } from "../context/AuthContext";

function SmallStat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-[#262a36] bg-[#11141b] p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#6f7484]">
            {label}
          </div>

          <div className="mt-2 font-display text-2xl font-bold text-white">
            {value}
          </div>

          {hint && (
            <div className="mt-1 text-xs text-[#a8adbd]">{hint}</div>
          )}
        </div>

        <div className="rounded-xl bg-[#ff4458]/10 p-2.5 text-[#ff4458]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function paymentStatusClass(status) {
  if (status === "paid") {
    return "bg-[#32d583]/15 text-[#32d583]";
  }

  if (["failed", "canceled", "expired"].includes(status)) {
    return "bg-[#fd267a]/15 text-[#fd267a]";
  }

  return "bg-[#fdb022]/15 text-[#fdb022]";
}

export default function AdminMarketingInsights() {
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userRole, setUserRole] = useState("");

  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [paymentPackage, setPaymentPackage] = useState("");

  const [salesPeriod, setSalesPeriod] = useState("day");
  const [salesData, setSalesData] = useState([]);

  const [newsletter, setNewsletter] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const [loading, setLoading] = useState(true);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionDryRun, setRetentionDryRun] = useState(true);

  const loadUsers = useCallback(async () => {
    const params = new URLSearchParams({
      limit: "200",
    });

    if (userSearch.trim()) {
      params.set("search", userSearch.trim());
    }

    if (userRole) {
      params.set("role", userRole);
    }

    const response = await axios.get(
      `${API}/admin/marketing/users?${params.toString()}`,
      { headers: authHeaders() }
    );

    setUsers(response.data.items || []);
    setUsersTotal(response.data.total || 0);
  }, [userSearch, userRole]);

  const loadPayments = useCallback(async () => {
    const params = new URLSearchParams({
      limit: "200",
    });

    if (paymentStatus) {
      params.set("status", paymentStatus);
    }

    if (paymentPackage) {
      params.set("package_id", paymentPackage);
    }

    const response = await axios.get(
      `${API}/admin/marketing/payments?${params.toString()}`,
      { headers: authHeaders() }
    );

    setPayments(response.data.items || []);
    setPaymentsTotal(response.data.total || 0);
  }, [paymentStatus, paymentPackage]);

  const loadSales = useCallback(async () => {
    const response = await axios.get(
      `${API}/admin/marketing/sales-chart?period=${salesPeriod}`,
      { headers: authHeaders() }
    );

    setSalesData(response.data.data || []);
  }, [salesPeriod]);

  const loadMarketingData = useCallback(async () => {
    setLoading(true);

    try {
      const [newsletterResponse, campaignsResponse] = await Promise.all([
        axios.get(`${API}/admin/marketing/newsletter/analytics`, {
          headers: authHeaders(),
        }),
        axios.get(`${API}/admin/marketing/campaigns`, {
          headers: authHeaders(),
        }),
      ]);

      setNewsletter(newsletterResponse.data);
      setCampaigns(campaignsResponse.data.campaigns || []);

      await Promise.all([
        loadUsers(),
        loadPayments(),
        loadSales(),
      ]);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Nie udało się pobrać rozszerzonych danych marketingowych"
      );
    } finally {
      setLoading(false);
    }
  }, [loadUsers, loadPayments, loadSales]);

  useEffect(() => {
    void loadMarketingData();
  }, [loadMarketingData]);

  useEffect(() => {
    void loadSales();
  }, [loadSales]);

  const revenueShown = useMemo(
    () =>
      salesData.reduce(
        (sum, item) => sum + Number(item.revenue || 0),
        0
      ),
    [salesData]
  );

  const runRetention = async () => {
    if (
      !retentionDryRun &&
      !window.confirm(
        "Uruchomić prawdziwą kampanię powrotną i wysłać wiadomości?"
      )
    ) {
      return;
    }

    setRetentionLoading(true);

    try {
      const response = await axios.post(
        `${API}/admin/marketing/campaigns/retention/run`,
        {
          dry_run: retentionDryRun,
          limit: 200,
        },
        { headers: authHeaders() }
      );

      console.log("Retention result:", response.data);

      toast.success(
        retentionDryRun
          ? "Test kampanii powrotnej został wykonany"
          : "Kampania powrotna została uruchomiona"
      );

      await loadMarketingData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Nie udało się uruchomić kampanii"
      );
    } finally {
      setRetentionLoading(false);
    }
  };

  if (loading && !newsletter) {
    return (
      <div className="mt-8 flex min-h-52 items-center justify-center dc-card">
        <Loader2 className="h-7 w-7 animate-spin text-[#ff4458]" />
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-8">
      {/* Newsletter analytics */}
      <section className="dc-card p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-2xl bg-[#ff4458]/10 p-3 text-[#ff4458]">
            <MailCheck className="h-5 w-5" />
          </div>

          <div>
            <h2 className="font-display text-xl font-bold text-white">
              Statystyki newslettera
            </h2>

            <p className="mt-1 text-xs text-[#a8adbd]">
              Wysłane wiadomości, błędy, otwarcia i kliknięcia.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <SmallStat
            icon={MailCheck}
            label="Wysłane"
            value={newsletter?.sent || 0}
          />

          <SmallStat
            icon={Activity}
            label="Błędy"
            value={newsletter?.failed || 0}
          />

          <SmallStat
            icon={UserCheck}
            label="Otwarcia"
            value={newsletter?.opens || 0}
            hint={`${newsletter?.open_rate || 0}%`}
          />

          <SmallStat
            icon={MousePointerClick}
            label="Kliknięcia"
            value={newsletter?.clicks || 0}
            hint={`${newsletter?.click_rate || 0}%`}
          />

          <SmallStat
            icon={Users}
            label="Wypisani"
            value={newsletter?.unsubscribed || 0}
          />
        </div>

        <div className="mt-4 rounded-xl border border-[#fdb022]/20 bg-[#fdb022]/5 px-4 py-3 text-xs leading-relaxed text-[#a8adbd]">
          Otwarcia i kliknięcia będą równe 0, dopóki nie dodamy
          śledzenia wiadomości za pomocą piksela i linków przekierowujących.
        </div>
      </section>

      {/* Sales chart */}
      <section className="dc-card p-6">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-white">
              Sprzedaż
            </h2>

            <p className="mt-1 text-xs text-[#a8adbd]">
              Przychód w wybranym okresie:{" "}
              <span className="font-semibold text-white">
                {revenueShown.toFixed(2)} PLN
              </span>
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ["day", "Dzień"],
              ["week", "Tydzień"],
              ["month", "Miesiąc"],
              ["year", "Rok"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setSalesPeriod(value)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition ${
                  salesPeriod === value
                    ? "dc-gradient-bg text-white"
                    : "border border-[#303545] bg-[#11141b] text-[#a8adbd] hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-80 w-full">
          {salesData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={salesData}
                margin={{
                  top: 10,
                  right: 20,
                  left: 0,
                  bottom: 10,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#262a36"
                />

                <XAxis
                  dataKey="period"
                  stroke="#6f7484"
                  tick={{ fontSize: 11 }}
                />

                <YAxis
                  stroke="#6f7484"
                  tick={{ fontSize: 11 }}
                />

                <Tooltip
                  contentStyle={{
                    background: "#151821",
                    border: "1px solid #303545",
                    borderRadius: "12px",
                  }}
                  labelStyle={{ color: "#ffffff" }}
                  formatter={(value, name) => {
                    if (name === "Przychód") {
                      return [`${Number(value).toFixed(2)} PLN`, name];
                    }

                    return [value, name];
                  }}
                />

                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Przychód"
                  stroke="#ff4458"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                  activeDot={{ r: 6 }}
                />

                <Line
                  type="monotone"
                  dataKey="payments"
                  name="Płatności"
                  stroke="#fdb022"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-[#303545] text-sm text-[#6f7484]">
              Brak opłaconych transakcji w tym okresie.
            </div>
          )}
        </div>
      </section>

      {/* CRM users */}
      <section className="dc-card p-6">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-white">
              Użytkownicy
            </h2>

            <p className="mt-1 text-xs text-[#a8adbd]">
              Znalezionych kont: {usersTotal}
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6f7484]" />

              <input
                value={userSearch}
                onChange={(event) =>
                  setUserSearch(event.target.value)
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void loadUsers();
                  }
                }}
                placeholder="E-mail lub nazwa"
                className="rounded-full border border-[#2a2f3d] bg-[#0f1117] py-2.5 pl-9 pr-4 text-sm text-white outline-none focus:border-[#ff4458]"
              />
            </div>

            <select
              value={userRole}
              onChange={(event) => setUserRole(event.target.value)}
              className="rounded-full border border-[#2a2f3d] bg-[#0f1117] px-4 py-2.5 text-sm text-white outline-none"
            >
              <option value="">Wszystkie role</option>
              <option value="user">Użytkownicy</option>
              <option value="admin">Administratorzy</option>
            </select>

            <button
              onClick={loadUsers}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#303545] px-4 py-2.5 text-sm text-white"
            >
              <Search className="h-4 w-4" />
              Szukaj
            </button>
          </div>
        </div>

        <div className="max-h-[520px] overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#262a36] text-xs uppercase tracking-wider text-[#6f7484]">
                <th className="px-3 py-3">Użytkownik</th>
                <th className="px-3 py-3">Rola</th>
                <th className="px-3 py-3">Utworzono</th>
                <th className="px-3 py-3">Ostatnie logowanie</th>
              </tr>
            </thead>

            <tbody>
              {users.map((user) => (
                <tr
                  key={user.user_id || user.id || user.email}
                  className="border-b border-[#20242f] text-[#d7dae3]"
                >
                  <td className="px-3 py-4">
                    <div className="font-semibold text-white">
                      {user.name || "Bez nazwy"}
                    </div>

                    <div className="mt-1 text-xs text-[#6f7484]">
                      {user.email || "—"}
                    </div>
                  </td>

                  <td className="px-3 py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        user.role === "admin"
                          ? "bg-[#fdb022]/15 text-[#fdb022]"
                          : "bg-[#32d583]/15 text-[#32d583]"
                      }`}
                    >
                      {user.role || "user"}
                    </span>
                  </td>

                  <td className="px-3 py-4">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleString()
                      : "—"}
                  </td>

                  <td className="px-3 py-4">
                    {user.last_login_at
                      ? new Date(user.last_login_at).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              ))}

              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-10 text-center text-[#6f7484]"
                  >
                    Nie znaleziono użytkowników.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Payments */}
      <section className="dc-card p-6">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-white">
              Płatności Stripe
            </h2>

            <p className="mt-1 text-xs text-[#a8adbd]">
              Liczba transakcji: {paymentsTotal}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={paymentStatus}
              onChange={(event) =>
                setPaymentStatus(event.target.value)
              }
              className="rounded-full border border-[#2a2f3d] bg-[#0f1117] px-4 py-2.5 text-sm text-white"
            >
              <option value="">Wszystkie statusy</option>
              <option value="paid">Opłacone</option>
              <option value="initiated">Rozpoczęte</option>
              <option value="unpaid">Nieopłacone</option>
              <option value="failed">Błąd</option>
            </select>

            <select
              value={paymentPackage}
              onChange={(event) =>
                setPaymentPackage(event.target.value)
              }
              className="rounded-full border border-[#2a2f3d] bg-[#0f1117] px-4 py-2.5 text-sm text-white"
            >
              <option value="">Wszystkie pakiety</option>
              <option value="single">1 kredyt</option>
              <option value="pack10">10 kredytów</option>
              <option value="monthly">
                15 kredytów miesięcznie
              </option>
            </select>

            <button
              onClick={loadPayments}
              className="inline-flex items-center gap-2 rounded-full border border-[#303545] px-4 py-2.5 text-sm text-white"
            >
              <RefreshCw className="h-4 w-4" />
              Filtruj
            </button>
          </div>
        </div>

        <div className="max-h-[520px] overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#262a36] text-xs uppercase tracking-wider text-[#6f7484]">
                <th className="px-3 py-3">Data</th>
                <th className="px-3 py-3">Pakiet</th>
                <th className="px-3 py-3">Kwota</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Kupon</th>
                <th className="px-3 py-3">Sesja</th>
              </tr>
            </thead>

            <tbody>
              {payments.map((payment) => (
                <tr
                  key={payment.session_id}
                  className="border-b border-[#20242f] text-[#d7dae3]"
                >
                  <td className="px-3 py-4">
                    {payment.created_at
                      ? new Date(payment.created_at).toLocaleString()
                      : "—"}
                  </td>

                  <td className="px-3 py-4">
                    {payment.package_id || "—"}
                  </td>

                  <td className="px-3 py-4 font-semibold text-white">
                    {Number(payment.amount || 0).toFixed(2)}{" "}
                    {(payment.currency || "pln").toUpperCase()}
                  </td>

                  <td className="px-3 py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${paymentStatusClass(
                        payment.payment_status
                      )}`}
                    >
                      {payment.payment_status || "—"}
                    </span>
                  </td>

                  <td className="px-3 py-4 font-mono text-xs">
                    {payment.coupon_code || "—"}
                  </td>

                  <td className="max-w-48 truncate px-3 py-4 font-mono text-xs text-[#6f7484]">
                    {payment.session_id || "—"}
                  </td>
                </tr>
              ))}

              {payments.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-10 text-center text-[#6f7484]"
                  >
                    Brak transakcji.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Campaigns */}
      <section className="dc-card p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-2xl bg-[#fdb022]/10 p-3 text-[#fdb022]">
            <Play className="h-5 w-5" />
          </div>

          <div>
            <h2 className="font-display text-xl font-bold text-white">
              Kampanie automatyczne
            </h2>

            <p className="mt-1 text-xs text-[#a8adbd]">
              Podgląd istniejących automatów i ręczne uruchamianie.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="rounded-2xl border border-[#262a36] bg-[#11141b] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-white">
                    {campaign.name}
                  </div>

                  <div className="mt-1 text-xs text-[#a8adbd]">
                    {campaign.description ||
                      (campaign.discount
                        ? `Rabat: ${campaign.discount}%`
                        : "Kampania aktywna")}
                  </div>
                </div>

                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    campaign.enabled
                      ? "bg-[#32d583]/15 text-[#32d583]"
                      : "bg-[#fd267a]/15 text-[#fd267a]"
                  }`}
                >
                  {campaign.enabled ? "Włączona" : "Wyłączona"}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-2xl border border-[#fdb022]/20 bg-[#fdb022]/5 p-4">
          <label className="flex items-center gap-3 text-sm text-white">
            <input
              type="checkbox"
              checked={retentionDryRun}
              onChange={(event) =>
                setRetentionDryRun(event.target.checked)
              }
              className="h-4 w-4"
            />

            Tylko test — nie wysyłaj prawdziwych wiadomości
          </label>

          <button
            onClick={runRetention}
            disabled={retentionLoading}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#fdb022] px-5 py-3 text-sm font-bold text-[#111318] disabled:opacity-50"
          >
            {retentionLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}

            Uruchom kampanię powrotną
          </button>
        </div>
      </section>
    </div>
  );
}
